import { createRoute } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { rootRoute } from "./__root";
import { PageHeader } from "../components/ui/PageHeader/PageHeader";
import { Card } from "../components/ui/Card/Card";
import { Button } from "../components/ui/Button/Button";
import { Badge } from "../components/ui/Badge/Badge";
import { FormField } from "../components/ui/FormField/FormField";
import { Input } from "../components/ui/Input/Input";
import { PasswordInput } from "../components/ui/PasswordInput/PasswordInput";
import { Avatar } from "../components/ui/Avatar/Avatar";
import { api } from "../lib/api";
import { useAuth } from "../lib/use-auth";
import styles from "./Account.module.css";

export const route = createRoute({
	getParentRoute: () => rootRoute,
	path: "/account",
	component: AccountPage,
});

function validatePassword(password: string, t: (key: string) => string): string | null {
	if (password.length < 12) return t("passwordRequirements");
	if (!/[A-Z]/.test(password)) return t("passwordRequirements");
	if (!/[a-z]/.test(password)) return t("passwordRequirements");
	if (!/[0-9]/.test(password)) return t("passwordRequirements");
	if (!/[^A-Za-z0-9]/.test(password)) return t("passwordRequirements");
	return null;
}

function roleBadgeVariant(role: string): "primary" | "default" | "muted" {
	if (role === "admin") return "primary";
	if (role === "customer") return "muted";
	return "default";
}

function AccountPage() {
	const { t } = useTranslation();
	const { user } = useAuth();
	const [form, setForm] = useState({ currentPassword: "", newPassword: "" });
	const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
	const [error, setError] = useState("");
	const [success, setSuccess] = useState(false);
	const [saving, setSaving] = useState(false);

	const [ncForm, setNcForm] = useState({ username: "", password: "" });
	const [ncSavedUsername, setNcSavedUsername] = useState<string | null>(null);
	const [ncError, setNcError] = useState("");
	const [ncSuccess, setNcSuccess] = useState(false);
	const [ncSaving, setNcSaving] = useState(false);
	const [ncTestResult, setNcTestResult] = useState<{ ok: boolean; message: string } | null>(null);
	const [ncTesting, setNcTesting] = useState(false);

	const [mailPassword, setMailPassword] = useState("");
	const [mailConfigured, setMailConfigured] = useState(false);
	const [mailError, setMailError] = useState("");
	const [mailSuccess, setMailSuccess] = useState(false);
	const [mailSaving, setMailSaving] = useState(false);
	const [mailTestResult, setMailTestResult] = useState<{ ok: boolean; message: string } | null>(
		null,
	);
	const [mailTesting, setMailTesting] = useState(false);

	const handleNcTest = async () => {
		setNcTestResult(null);
		setNcTesting(true);
		try {
			const res = await api.get<{ ok: boolean; message: string }>("/api/nextcloud/test");
			setNcTestResult(res);
		} catch (err) {
			setNcTestResult({
				ok: false,
				message: err instanceof Error ? err.message : "Bağlantı hatası",
			});
		}
		setNcTesting(false);
	};

	useEffect(() => {
		api
			.get<{ nextcloudUsername?: string | null; mailConfigured?: boolean }>("/auth/me")
			.then((me) => {
				if (me.nextcloudUsername) {
					setNcSavedUsername(me.nextcloudUsername);
					setNcForm((f) => ({ ...f, username: me.nextcloudUsername ?? "" }));
				}
				setMailConfigured(!!me.mailConfigured);
			})
			.catch(() => {});
	}, []);

	const handleMailSubmit = async () => {
		setMailError("");
		setMailSuccess(false);
		if (!mailPassword) {
			setMailError("Mail şifresi zorunludur.");
			return;
		}
		setMailSaving(true);
		try {
			await api.patch("/auth/mail", { mailPassword });
			setMailConfigured(true);
			setMailSuccess(true);
			setMailPassword("");
		} catch (err) {
			setMailError(err instanceof Error ? err.message : "Kaydedilemedi");
		}
		setMailSaving(false);
	};

	const handleMailTest = async () => {
		setMailTestResult(null);
		setMailTesting(true);
		try {
			const res = await api.get<{ ok: boolean; message: string }>("/auth/mail/test");
			setMailTestResult(res);
		} catch (err) {
			setMailTestResult({
				ok: false,
				message: err instanceof Error ? err.message : "Bağlantı hatası",
			});
		}
		setMailTesting(false);
	};

	const handleNcSubmit = async () => {
		setNcError("");
		setNcSuccess(false);
		if (!ncForm.username || !ncForm.password) {
			setNcError("Kullanıcı adı ve uygulama şifresi zorunludur.");
			return;
		}
		setNcSaving(true);
		try {
			await api.patch("/auth/nextcloud", {
				nextcloudUsername: ncForm.username,
				nextcloudPassword: ncForm.password,
			});
			setNcSavedUsername(ncForm.username);
			setNcSuccess(true);
			setNcForm((prev) => ({ ...prev, password: "" }));
		} catch (err) {
			setNcError(err instanceof Error ? err.message : "Kaydedilemedi");
		}
		setNcSaving(false);
	};

	const handleSubmit = async () => {
		setError("");
		setSuccess(false);
		const errors: Record<string, string> = {};
		if (!form.currentPassword) errors.currentPassword = t("requiredField");
		if (!form.newPassword) errors.newPassword = t("requiredField");
		setFieldErrors(errors);
		if (Object.keys(errors).length > 0) return;

		const passwordError = validatePassword(form.newPassword, t);
		if (passwordError) {
			setError(passwordError);
			return;
		}

		setSaving(true);
		try {
			await api.post("/auth/change-password", {
				currentPassword: form.currentPassword,
				newPassword: form.newPassword,
			});
			setForm({ currentPassword: "", newPassword: "" });
			setSuccess(true);
		} catch (err) {
			const message = err instanceof Error ? err.message : t("changePasswordFailed");
			setError(message);
		}
		setSaving(false);
	};

	return (
		<div className={styles.page}>
			<PageHeader title={t("myAccount")} />
			<Card header={t("accountInfo")}>
				<div className={styles.profile}>
					<Avatar
						firstName={user?.firstName}
						lastName={user?.lastName}
						email={user?.email}
						size={56}
					/>
					<div className={styles.profileInfo}>
						<div className={styles.profileName}>
							{user?.firstName} {user?.lastName}
						</div>
						<div className={styles.profileEmail}>{user?.email}</div>
						{user && <Badge variant={roleBadgeVariant(user.role)}>{t(user.role)}</Badge>}
					</div>
				</div>
			</Card>
			<Card header={t("changePassword")}>
				<div className={styles.form}>
					{error && <div className={styles.error}>{error}</div>}
					{success && <div className={styles.success}>{t("passwordChanged")}</div>}
					<FormField label={t("currentPassword")} required>
						<PasswordInput
							value={form.currentPassword}
							onChange={(e) => setForm({ ...form, currentPassword: e.target.value })}
							error={fieldErrors.currentPassword ?? ""}
						/>
					</FormField>
					<FormField label={t("newPassword")} required>
						<PasswordInput
							value={form.newPassword}
							onChange={(e) => setForm({ ...form, newPassword: e.target.value })}
							error={fieldErrors.newPassword ?? ""}
						/>
						<span className={styles.hint}>{t("passwordRequirements")}</span>
					</FormField>
					<Button className={styles.actions} onClick={handleSubmit} isLoading={saving}>
						{t("changePassword")}
					</Button>
				</div>
			</Card>
			<Card header="Nextcloud Bağlantısı">
				<div className={styles.form}>
					{ncSavedUsername && (
						<div className={styles.success}>
							Bağlı hesap: <strong>{ncSavedUsername}</strong>
						</div>
					)}
					{ncError && <div className={styles.error}>{ncError}</div>}
					{ncSuccess && <div className={styles.success}>Nextcloud bilgileri kaydedildi.</div>}
					<FormField label="Nextcloud Kullanıcı Adı" required>
						<Input
							value={ncForm.username}
							onChange={(e) => setNcForm((f) => ({ ...f, username: e.target.value }))}
							placeholder="kullaniciadi"
						/>
					</FormField>
					<FormField label="Uygulama Şifresi" required>
						<PasswordInput
							value={ncForm.password}
							onChange={(e) => setNcForm((f) => ({ ...f, password: e.target.value }))}
							error=""
						/>
					</FormField>
					{ncTestResult && (
						<div className={ncTestResult.ok ? styles.success : styles.error}>
							{ncTestResult.message}
						</div>
					)}
					<div className={styles.ncButtons}>
						<Button onClick={handleNcSubmit} isLoading={ncSaving}>
							Kaydet
						</Button>
						<Button variant="secondary" onClick={handleNcTest} isLoading={ncTesting}>
							Test Et
						</Button>
					</div>
				</div>
			</Card>
			<Card header="Mail Ayarları">
				<div className={styles.form}>
					{mailConfigured && !mailSuccess && (
						<div className={styles.success}>
							Bağlı hesap: <strong>{user?.email}</strong>
						</div>
					)}
					{mailError && <div className={styles.error}>{mailError}</div>}
					{mailSuccess && <div className={styles.success}>Mail şifresi kaydedildi.</div>}
					<FormField label="Mail Şifresi" required>
						<PasswordInput
							value={mailPassword}
							onChange={(e) => setMailPassword(e.target.value)}
							error=""
						/>
						<span className={styles.hint}>
							Teklif e-postaları bu şifreyle kendi hesabınızdan gönderilir.
						</span>
					</FormField>
					{mailTestResult && (
						<div className={mailTestResult.ok ? styles.success : styles.error}>
							{mailTestResult.message}
						</div>
					)}
					<div className={styles.ncButtons}>
						<Button onClick={handleMailSubmit} isLoading={mailSaving}>
							Kaydet
						</Button>
						<Button variant="secondary" onClick={handleMailTest} isLoading={mailTesting}>
							Test Et
						</Button>
					</div>
				</div>
			</Card>
		</div>
	);
}
